import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { isEqual } from 'lodash-es';
import { Uuid } from '@/api/types';
import { UserRole } from '@/api/apis/user';
import { CaseResourceCombined, OpoContactWithPrimary } from '@/api/apis/case';
import { useMutateResourcesAssignment } from './useMutateResourcesAssignment/useMutateResourcesAssignment';
import {
  mergeAssignDetachWithExternalUsers,
  mergeAssignDetachWithExternalIds,
  mergeAssignDetachWithExternalContacts,
  mergeUsersByRoleAssignDetach,
  updateAssignDetachIdsByRole,
  updateAssignDetachIdsFlat,
  updateAssignDetachOpoContacts,
  AssignDetachState,
  AssignDetachUsersState,
  showRoleDetachWarning,
  showOpoContactDetachWarning,
} from '@/utils/helpers/resources.helpers';
import { roleNames } from '@/constants/user.constants';
import { RoleKeyedPartial } from '@/types';

type UseAssignUsersToCase = {
  selectedRole?: UserRole;
  assignedUsersByRoles?: RoleKeyedPartial<string[]>;
  assignedContacts?: OpoContactWithPrimary[];
  clinicalSpecialistIds?: string[];
  surgeonIds?: string[];
  ocsConsoles?: CaseResourceCombined[];
  caseId: Uuid;
  useExtendedResourcesInfo?: boolean;
  enableCaseByIdQuery?: boolean;
  isOpoCase?: boolean;
  onSetPrimaryModalOpen?: () => void;
  onSave: () => void;
  isPrimaryUnassigned?: boolean;
};

export const useAssignResourcesToCase = ({
  selectedRole,
  assignedUsersByRoles = {},
  assignedContacts = [],
  clinicalSpecialistIds = [],
  surgeonIds = [],
  ocsConsoles: assignedDevices = [],
  caseId,
  useExtendedResourcesInfo = true,
  enableCaseByIdQuery = true,
  isOpoCase,
  isPrimaryUnassigned,
  onSetPrimaryModalOpen,
  onSave,
}: UseAssignUsersToCase) => {
  const assignedDevicesIds = useMemo(() => assignedDevices?.map((d) => d.resourceId) || [], [assignedDevices]);
  const [pendingUsers, setPendingUsers] = useState<AssignDetachUsersState>(() => {
    const state: AssignDetachUsersState = {};
    Object.keys(assignedUsersByRoles).forEach((role) => {
      state[role as UserRole] = { assign: [], detach: [] };
    });
    return state;
  });

  const [pendingOpoContacts, setPendingOpoContacts] = useState<AssignDetachState<OpoContactWithPrimary>>(() => ({
    assign: [],
    detach: [],
  }));
  const [pendingClinicalSpecialistIds, setPendingClinicalSpecialistIds] = useState<AssignDetachState>(() => ({
    assign: [],
    detach: [],
  }));
  const [pendingSurgeonIds, setPendingSurgeonIds] = useState<AssignDetachState>(() => ({ assign: [], detach: [] }));
  const [pendingDeviceIds, setPendingDeviceIds] = useState<AssignDetachState>(() => ({ assign: [], detach: [] }));

  const prevAssignedUsersByRoles = useRef(assignedUsersByRoles);
  const prevAssignedContacts = useRef(assignedContacts);
  const prevClinicalSpecialistIds = useRef(clinicalSpecialistIds);
  const prevSurgeonIds = useRef(surgeonIds);
  const prevAssignedDeviceIds = useRef(assignedDevicesIds);

  useEffect(() => {
    if (!isEqual(prevAssignedUsersByRoles.current, assignedUsersByRoles)) {
      setPendingUsers((pending) => mergeAssignDetachWithExternalUsers(pending, assignedUsersByRoles));
      prevAssignedUsersByRoles.current = assignedUsersByRoles;
    }
  }, [assignedUsersByRoles]);

  useEffect(() => {
    if (!isEqual(prevAssignedContacts.current, assignedContacts)) {
      setPendingOpoContacts((pending) => mergeAssignDetachWithExternalContacts(pending, assignedContacts));
      prevAssignedContacts.current = assignedContacts;
    }
  }, [assignedContacts]);

  useEffect(() => {
    if (!isEqual(prevClinicalSpecialistIds.current, clinicalSpecialistIds)) {
      setPendingClinicalSpecialistIds((pending) => mergeAssignDetachWithExternalIds(pending, clinicalSpecialistIds));
      prevClinicalSpecialistIds.current = clinicalSpecialistIds;
    }
  }, [clinicalSpecialistIds]);

  useEffect(() => {
    if (!isEqual(prevSurgeonIds.current, surgeonIds)) {
      setPendingSurgeonIds((pending) => mergeAssignDetachWithExternalIds(pending, surgeonIds));
      prevSurgeonIds.current = surgeonIds;
    }
  }, [surgeonIds]);

  useEffect(() => {
    if (!isEqual(prevAssignedDeviceIds.current, assignedDevicesIds)) {
      setPendingDeviceIds((pending) => mergeAssignDetachWithExternalIds(pending, assignedDevicesIds));
      prevAssignedDeviceIds.current = assignedDevicesIds;
    }
  }, [assignedDevicesIds]);

  const usersToAssign = useMemo(
    () => mergeUsersByRoleAssignDetach(assignedUsersByRoles, pendingUsers),
    [assignedUsersByRoles, pendingUsers],
  );

  const opoContactsToAssign = useMemo(
    () => [
      ...assignedContacts.filter((c) => !pendingOpoContacts.detach.find((i) => i.opoContactId === c.opoContactId)),
      ...pendingOpoContacts.assign,
    ],
    [assignedContacts, pendingOpoContacts],
  );
  const newClinicalSpecialistIds = useMemo(
    () =>
      Array.from(new Set([...clinicalSpecialistIds, ...pendingClinicalSpecialistIds.assign])).filter(
        (id) => !pendingClinicalSpecialistIds.detach.includes(id),
      ),
    [clinicalSpecialistIds, pendingClinicalSpecialistIds],
  );
  const newSurgeonIds = useMemo(
    () =>
      Array.from(new Set([...surgeonIds, ...pendingSurgeonIds.assign])).filter(
        (id) => !pendingSurgeonIds.detach.includes(id),
      ),
    [surgeonIds, pendingSurgeonIds],
  );
  const newDevicesIds = useMemo(
    () =>
      Array.from(new Set([...assignedDevicesIds, ...pendingDeviceIds.assign])).filter(
        (id) => !pendingDeviceIds.detach.includes(id),
      ),
    [assignedDevicesIds, pendingDeviceIds],
  );

  const { isSubmitting, saveAssignment } = useMutateResourcesAssignment({
    caseId,
    assignedUsersByRoles,
    usersToAssign,
    clinicalSpecialistIds,
    newClinicalSpecialistIds,
    surgeonIds,
    newSurgeonIds,
    assignedDevicesIds,
    newDevicesIds,
    assignedContacts,
    opoContactsToAssign,
    useExtendedResourcesInfo,
    enableCaseByIdQuery,
    isOpoCase,
    isPrimaryUnassigned,
    onSetPrimaryModalOpen,
    onSave,
  });

  const onUserAssign = useCallback(
    ({ id = '' }: { id: string }) => {
      setPendingUsers((pending) => {
        const updated = updateAssignDetachIdsByRole(pending, id, selectedRole!, assignedUsersByRoles);
        const nextUsers = mergeUsersByRoleAssignDetach(assignedUsersByRoles, updated);
        if (selectedRole) {
          showRoleDetachWarning(selectedRole, nextUsers, roleNames);
        }
        return updated;
      });
    },
    [setPendingUsers, selectedRole, assignedUsersByRoles],
  );

  const onOpoContactAssign = useCallback(
    ({ id }: { id: string }) => {
      setPendingOpoContacts((pending) => {
        const updated = updateAssignDetachOpoContacts(pending, id, assignedContacts);
        const nextContacts = [
          ...assignedContacts.filter((c) => !updated.detach.find((i) => i.opoContactId === c.opoContactId)),
          ...updated.assign,
        ];
        showOpoContactDetachWarning(nextContacts);
        return updated;
      });
    },
    [setPendingOpoContacts, assignedContacts],
  );

  const onClinicalSpecialistReserve = (id: string) => {
    setPendingClinicalSpecialistIds((pending) => updateAssignDetachIdsFlat(pending, id, clinicalSpecialistIds));
  };

  const onSurgeonReserve = (id: string) => {
    setPendingSurgeonIds((pending) => updateAssignDetachIdsFlat(pending, id, surgeonIds));
  };

  const onDeviceReserve = ({ id }: { id: string }) => {
    setPendingDeviceIds((pending) => ({
      ...pending,
      assign: pending.assign.includes(id) ? [] : [id],
    }));
  };

  return {
    onUserAssign,
    onOpoContactAssign,
    onDeviceReserve,
    onClinicalSpecialistReserve,
    onSurgeonReserve,
    saveAssignment,
    opoContactsToAssign,
    newClinicalSpecialistIds,
    newSurgeonIds,
    setPendingSurgeonIds,
    pendingSurgeonIds,
    newDevicesIds,
    isSubmitting,
    pendingUsers,
    setPendingUsers,
    usersToAssign,
  };
};
